import { usePendingTasks } from "@/lib/taskChanges";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Mic, Loader2, MapPin, CheckCircle2, Circle, Pencil, Trash2 } from "lucide-react";
import AvatarViewer from "@/components/AvatarViewer";
import VyvEnergy from "@/components/vyv/VyvEnergy";
import {
  MINI_MIRROR_IS_AVATURN_MODEL,
  MINI_MIRROR_SCALE,
  MINI_MIRROR_LOOK_AT_Y,
  MINI_MIRROR_CAMERA_DISTANCE,
  MINI_MIRROR_FRAMING,
} from "@/components/avatar/miniMirrorCalibration";
import { useAvatar } from "@/hooks/useAvatar";
import { useMirrorSurface } from "@/hooks/useMirrorSurface";
import { useLanguage } from "@/i18n/LanguageProvider";
import { localeTagFor, type Lang } from "@/i18n/locale";
import { isQuotaError, parsePaymentErrorFromResponse, QuotaError } from "@/lib/quotaError";
import { usePaymentErrorHandler } from "@/hooks/usePaymentErrorHandler";
import { useIsMobile } from "@/hooks/use-mobile";
import { getAnimationUrl } from "@/config/avatarAnimations";
import { agendaOwnerSession, type LocalAgendaReceiptReference, type LocalAgendaActionPrefill } from "@/lib/agendaExecutor";
import { LocalAgendaActionModal } from "@/components/mirror/LocalAgendaActionModal";
import { agendaCaptureHandler, agendaActionCaptureHandler, createAgendaCaptureHandler } from "@/lib/capture/handlers/agendaCaptureHandler";
import { confirmMirrorAgendaReceipt } from "@/lib/confirmMirrorAgendaReceipt";
import { publishAgendaRefresh } from "@/lib/agendaChanges";
import { useVoiceInputWhisper, type WhisperOwnerGuard } from "@/hooks/useVoiceInputWhisper";
import useVoice from "@/hooks/useVoice";
import { supabase } from "@/integrations/supabase/client";
import { mirrorAuthIdentity, sameMirrorAuthIdentity, type MirrorAuthIdentity } from "@/lib/mirrorAuthSession";
import ReportContentButton from "@/components/report/ReportContentButton";
import { REPORT_SURFACES } from "@/lib/report/surfaces";
import { resolveAssistantMessageId } from "@/lib/report/resolveAssistantMessageId";
import { useReportedContent } from "@/contexts/ReportedContentContext";
import { getOrCreateMirrorSession } from "@/lib/mirrorSession";
import { MiniMirrorLiveButton } from "@/components/common/MiniMirrorLiveButton";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription, DrawerClose } from "@/components/ui/drawer";
import { formatCountdown, formatEventMeta, formatEventSource } from "@/lib/durationFormat";
import { queryAgendaWindow } from "@/lib/agendaQueries";
import { isAllDay, eventStartDate, eventOccursOn, formatAllDayDates } from "@/lib/agendaDates";
import { agendaEventRange, localEventDayKey } from "@/lib/agendaEventRange";
import type { NextEvent } from "@/hooks/useAgendaSnapshot";
import { shouldSkipTTS } from "@/lib/ttsFilters";
import { useAutoDismiss, CARD_DISMISS_MS } from "@/hooks/useAutoDismiss";
import { toast } from "sonner";
import type { ElevenLabsAlignment } from "@/lib/visemeMapper";
import { CreateEventModal, type CreateEventPrefill } from "@/components/agenda/CreateEventModal";
import TaskModal from "@/components/TaskModal";
import { isSyncedTask, type Task, type UpdateTaskInput } from "@/hooks/useTasks";
import { snapshotForTask } from "@/lib/taskMutations";
import type { TaskSnapshot } from "@/lib/taskExecutor";
import { AnimatePresence, motion } from "framer-motion";
import type { ActionProposal } from "@/types/mirror";
import { isIOSNative } from "@/lib/platform";
import ImportedTaskActionModal from "@/components/mirror/ImportedTaskActionModal";

/**
 * Calendar hub mini-Mirror with voice press-to-talk.
 *
 * Adapted from src/components/brain/BrainMiniMirror.tsx — same 3-slot split,
 * iOS audio unlock pattern, mirror-chat SSE streaming. Differences:
 *   - cyan accent instead of purple
 *   - labels under `agenda.mirror.*` i18n namespace
 *   - module_context: "calendar" in the mirror-chat body
 *   - scorecard slot replaces Brain's CogGauge/COG_BARS
 *
 * Frozen avatar files NOT modified — uses the consumer-level AvatarViewer.
 */

const SILENT_MP3 = "data:audio/mpeg;base64,SUQzBAAAAAABEVRYWFgAAAAtAAADY29tbWVudABCaWdTb3VuZEJhbmsuY29tIC8gTGFTb25vdGhlcXVlLmNvbQBURU5DAAAABwAAA0xhbWUAAAAAAAAAAAAAAP/7kGQAAANUMEoFPeACNQV40KEAAA0gwX4UPYACNgV5Io5eBAgAADXCHP/7kmRAAADSBHIJaxiADNgFhw2EABN0Fch2dYAI2gWHDYSAAAANPJy//+5JkwAAA0wRyHZ2AAjYBYcNhAAAA0gRyHZ1gAjaBYcNhIAAAAf/+5JkCgAA0gRyHZ1gAjaBYcNhAAAA0gRyHZ1gAjYBYcNhIAAAPf/7kmQAAADSBHIdnWACNoFhw2EgAADSBHIdnWACNgFhw2EgAAA=";

type VoiceState = "idle" | "listening" | "processing" | "speaking" | "error";

interface AgendaMirrorContextValue {
  voiceState: VoiceState;
  lastResponse: string;
  // mirror_messages.id da resposta em `lastResponse`, resolvido depois do
  // stream (o SSE nao devolve id). null = ainda sem identidade estavel, e
  // entao o controle de denuncia nao aparece nesta resposta.
  lastResponseId: string | null;
  // Dismiss the lastResponse card now (tap-to-close). Auto-dismiss also runs on
  // the shared CARD_DISMISS_MS timer; this is the manual escape hatch.
  clearLastResponse: () => void;
  handleMicPress: () => Promise<void>;
  // Exposed so the avatar slot can wire lipsync (Bug A fix 12/05/26).
  audioElement: HTMLAudioElement | null;
  alignment: ElevenLabsAlignment | null;
}

const AgendaMirrorContext = createContext<AgendaMirrorContextValue | null>(null);

function useAgendaMirrorState(): AgendaMirrorContextValue {
  const ctx = useContext(AgendaMirrorContext);
  if (!ctx) {
    return {
      voiceState: "idle",
      lastResponse: "",
      // Fora do provider nao ha resposta e, portanto, nao ha id — o controle de
      // denuncia simplesmente nao aparece. Faltava aqui: o valor de fallback e o
      // que um consumidor mal montado recebe, e sem esta linha ele quebraria o
      // contrato do contexto em runtime, nao so no tipo.
      lastResponseId: null,
      clearLastResponse: () => {},
      handleMicPress: async () => {},
      audioElement: null,
      alignment: null,
    };
  }
  return ctx;
}

export function AgendaMirrorProvider({ children }: { children: ReactNode }) {
  const { lang, t } = useLanguage();
  const navigate = useNavigate();
  const handlePaymentError = usePaymentErrorHandler();
  const audioElementRef = useRef<HTMLAudioElement | null>(null);

  const whisper = useVoiceInputWhisper(lang);
  const tts = useVoice({ externalAudioElement: audioElementRef.current });

  const [voiceState, setVoiceState] = useState<VoiceState>("idle");
  const [lastResponse, setLastResponse] = useState<string>("");
  const [lastResponseId, setLastResponseId] = useState<string | null>(null);

  // Confirmation card must not linger or trap the voice loop. Auto-dismiss on
  // the shared timer (its OWN timer — never a route change); also tap-to-close.
  const clearLastResponse = useCallback(() => setLastResponse(""), []);
  useAutoDismiss(lastResponse, CARD_DISMISS_MS, clearLastResponse);

  // Stash `t` in a ref so the agenda-extract/INSERT callbacks below don't
  // need it in their useCallback dependency array (avoids re-creating the
  // closures on every i18n re-render).
  const tRef = useRef(t);
  useEffect(() => { tRef.current = t; }, [t]);

  const [agendaPrefill, setAgendaPrefill] = useState<CreateEventPrefill | null>(null);
  const [agendaModalOpen, setAgendaModalOpen] = useState(false);
  const [localAgendaPrefill, setLocalAgendaPrefill] = useState<LocalAgendaActionPrefill | null>(null);
  type OwnedTurn = WhisperOwnerGuard & { accessToken: string; assertCurrent: () => void };
  const ownerRef = useRef<string | null | undefined>(undefined), generation = useRef(0), mounted = useRef(false);
  const authIdentity = useRef<MirrorAuthIdentity | null | undefined>(undefined);
  const turnRef = useRef<OwnedTurn | null>(null), micLock = useRef(false), controller = useRef<AbortController | null>(null);
  const modalTurn = useRef<OwnedTurn | null>(null);
  const modalCapture = useRef<{ prefill: CreateEventPrefill | LocalAgendaActionPrefill; rawKey: string; turn: OwnedTurn } | null>(null);
  const stopRef = useRef(tts.stop), stopRecording = useRef(whisper.stopListening);
  stopRef.current = tts.stop; stopRecording.current = whisper.stopListening;
  const agendaResolvingRef = useRef(new Set<string>()), resolvedRawTextsRef = useRef(new Map<string, number>());
  useEffect(() => {
    mounted.current = true;
    const invalidate = () => {
      generation.current++; controller.current?.abort(); controller.current = null;
      const old = turnRef.current; turnRef.current = null; modalTurn.current = null; modalCapture.current = null;
      micLock.current = false; stopRef.current(); if (old) void stopRecording.current(old);
      agendaResolvingRef.current.clear(); resolvedRawTextsRef.current.clear();
      setVoiceState("idle"); setLastResponse(""); setLastResponseId(null); setAgendaPrefill(null); setAgendaModalOpen(false); setLocalAgendaPrefill(null);
    };
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const next = mirrorAuthIdentity(session), previous = authIdentity.current;
      authIdentity.current = next; ownerRef.current = next?.ownerId ?? null;
      // A valid initial observation finishes bootstrap without cancelling the
      // first click. Later account or auth-session changes revoke every turn.
      if (previous === undefined && next) return;
      if (!sameMirrorAuthIdentity(previous ?? null, next)) invalidate();
    });
    const epoch = generation;
    return () => { mounted.current = false; epoch.current++; controller.current?.abort(); stopRef.current(); const old = turnRef.current; if (old) void stopRecording.current(old); subscription.unsubscribe(); };
  }, []);

  const runChatStream = useCallback(async (userMessage: string, turn: OwnedTurn, sessionId: string) => {
    turn.assertCurrent(); const abort = new AbortController(); controller.current = abort;
    const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/mirror-chat`, {
      method: "POST", signal: abort.signal,
      headers: { "Content-Type": "application/json", Authorization: "Bearer " + turn.accessToken, apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY },
      body: JSON.stringify({ promptType: "chat", userId: turn.ownerId, sessionId, userMessage, language: lang, mirrorTone: "balanced", module_context: "calendar", platform: isIOSNative() ? "ios" : "web" }),
    }); turn.assertCurrent();
    if (res.status === 402) { const pe = await parsePaymentErrorFromResponse(res); turn.assertCurrent(); if (pe) throw new QuotaError(pe); }
    if (!res.ok || !res.body) throw new Error(`mirror-chat failed: ${res.status}`);
    const reader = res.body.getReader(), decoder = new TextDecoder();
    let accumulated = "", finalText = "", buffer = "", proposals: ActionProposal[] = [];
    try {
      for (;;) {
        turn.assertCurrent(); const { value, done } = await reader.read(); turn.assertCurrent(); if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let end: number;
        while ((end = buffer.indexOf("\n\n")) !== -1) {
          const chunk = buffer.slice(0, end); buffer = buffer.slice(end + 2);
          const event = chunk.split("\n").find(line => line.startsWith("event:"))?.slice(6).trim();
          const data = chunk.split("\n").find(line => line.startsWith("data:"))?.slice(5).trim();
          if (!data) continue;
          try { const payload = JSON.parse(data); if (event === "delta" && typeof payload.text === "string") accumulated += payload.text;
            if (event === "final") { if (typeof payload.response === "string") finalText = payload.response; if (Array.isArray(payload.proposals)) proposals = payload.proposals; }
          } catch { /* Malformed SSE is never permission to act. */ }
        }
      }
      return { text: finalText || accumulated, proposals };
    } finally { if (controller.current === abort) controller.current = null; if (!turn.isCurrent()) void reader.cancel().catch(() => {}); reader.releaseLock(); }
  }, [lang]);

  const resolveAgendaIntent = useCallback(async (intent: Extract<ActionProposal, { tool: "propose_agenda_event" | "propose_local_agenda_action" | "create_agenda_event" }>, turn: OwnedTurn, sessionId: string) => {
    turn.assertCurrent(); const actionId = intent.action_id;
    if (agendaResolvingRef.current.has(actionId)) return;
    const handler = intent.tool === "propose_agenda_event" ? agendaCaptureHandler : intent.tool === "create_agenda_event" ? createAgendaCaptureHandler : agendaActionCaptureHandler;
    if (!handler.isEnabled()) return;
    const raw = handler.getRawText(intent); if (!raw) return;
    const key = raw.trim().toLowerCase(), previous = resolvedRawTextsRef.current.get(key);
    if (previous !== undefined && Date.now() - previous < 5 * 60_000) return;
    agendaResolvingRef.current.add(actionId);
    let quotaSurfaced = false;
    const failedExtraction = () => {
      if (!turn.isCurrent()) return;
      // No review or writer was opened. Release only this failed proposal,
      // retaining duplicate SSE protection for pending and successful captures.
      agendaResolvingRef.current.delete(actionId);
      if (!quotaSurfaced) toast.error(tRef.current(intent.tool === "propose_agenda_event" || intent.tool === "create_agenda_event" || intent.args.action === "create" ? "agenda.failedToCreate" : "agendaAction.error"));
    };
    try {
      const prefill = await handler.extract(raw, intent, { lang, t: tRef.current, ownerId: turn.ownerId, sessionId, assertCurrent: turn.assertCurrent,
        surfaceQuota: async error => { turn.assertCurrent(); if (isQuotaError(error)) { quotaSurfaced = true; handlePaymentError(error.payment); return true; } return false; } });
      turn.assertCurrent(); if (!prefill) { failedExtraction(); return; }
      resolvedRawTextsRef.current.set(key, Date.now()); modalTurn.current = turn; modalCapture.current = { prefill, rawKey: key, turn };
      if (intent.tool === "propose_agenda_event") { setLocalAgendaPrefill(null); setAgendaPrefill(prefill as CreateEventPrefill); setAgendaModalOpen(true); }
      else { setAgendaPrefill(null); setAgendaModalOpen(false); setLocalAgendaPrefill(prefill as LocalAgendaActionPrefill); }
    } catch { if (turn.isCurrent()) { console.warn("[AgendaMiniMirror] extraction unavailable"); failedExtraction(); } }
  }, [lang, handlePaymentError]);

  const handleMicPress = useCallback(async () => {
    // iOS unlock remains synchronous in the gesture. No private content is played here.
    const audioEl = audioElementRef.current;
    if (audioEl) { try { audioEl.src = SILENT_MP3; void audioEl.play().then(() => audioEl.pause()).catch(() => {}); } catch { /* Gesture unlock may be unavailable. */ } }
    if (micLock.current) return;
    if (voiceState === "listening") {
      const turn = turnRef.current; if (!turn?.isCurrent()) return;
      micLock.current = true; setVoiceState("processing");
      try {
        const transcript = await whisper.stopListening(turn); turn.assertCurrent(); if (!transcript) { setVoiceState("idle"); return; }
        await agendaOwnerSession(turn.ownerId, turn.assertCurrent); turn.assertCurrent();
        const sessionId = await getOrCreateMirrorSession(turn.ownerId, { accessToken: turn.accessToken, assertCurrent: turn.assertCurrent }); turn.assertCurrent();
        const { text: response, proposals } = await runChatStream(transcript, turn, sessionId); turn.assertCurrent();
        setLastResponse(response); setLastResponseId(null);
        void resolveAssistantMessageId(sessionId, response, { userId: turn.ownerId, accessToken: turn.accessToken, assertCurrent: turn.assertCurrent }).then(id => { if (turn.isCurrent()) setLastResponseId(id); });
        const intents = proposals.filter((p): p is Extract<ActionProposal, { tool: "propose_agenda_event" | "propose_local_agenda_action" | "create_agenda_event" }> => p?.tool === "propose_agenda_event" || p?.tool === "propose_local_agenda_action" || p?.tool === "create_agenda_event");
        for (const intent of intents) { turn.assertCurrent(); void resolveAgendaIntent(intent, turn, sessionId); }
        if (shouldSkipTTS(response)) { setVoiceState("idle"); return; }
        turn.assertCurrent(); setVoiceState("speaking"); await tts.speak(response, { language: lang, ownerGuard: { accessToken: turn.accessToken, assertCurrent: turn.assertCurrent } }); turn.assertCurrent(); setVoiceState("idle");
      } catch (error) {
        if (!turn.isCurrent()) return;
        if (isQuotaError(error)) { handlePaymentError(error.payment); setVoiceState("idle"); return; }
        console.warn("[AgendaMiniMirror] voice unavailable"); setVoiceState("error"); setTimeout(() => { if (turn.isCurrent()) setVoiceState("idle"); }, 1500);
      } finally { if (turn.isCurrent()) micLock.current = false; }
      return;
    }
    if (voiceState !== "idle" && voiceState !== "error") return;
    generation.current++; controller.current?.abort(); stopRef.current(); setLocalAgendaPrefill(null); setAgendaPrefill(null); setAgendaModalOpen(false);
    const epoch = generation.current, current = () => mounted.current && epoch === generation.current;
    const assert = () => { if (!current()) throw new Error("invalid_session"); };
    micLock.current = true;
    try {
      assert(); const user = await supabase.auth.getUser(); assert();
      const uid = user.data.user?.id;
      if (user.error || !uid || ownerRef.current !== undefined && ownerRef.current !== uid) throw new Error("invalid_session");
      ownerRef.current = uid;
      const assertCurrent = () => { assert(); if (ownerRef.current !== uid) throw new Error("invalid_session"); };
      const session = await agendaOwnerSession(uid, assertCurrent); assertCurrent();
      const actor = mirrorAuthIdentity(session);
      if (!actor || actor.ownerId !== uid || authIdentity.current !== undefined && !sameMirrorAuthIdentity(authIdentity.current, actor)) throw new Error("invalid_session");
      if (authIdentity.current === undefined) authIdentity.current = actor;
      const isCurrent = () => current() && ownerRef.current === uid && sameMirrorAuthIdentity(authIdentity.current ?? null, actor);
      const assertTurn = () => { if (!isCurrent()) throw new Error("invalid_session"); };
      const turn: OwnedTurn = { ownerId: uid, authSessionId: actor.authSessionId, accessToken: actor.accessToken, isCurrent, assertCurrent: assertTurn };
      turnRef.current = turn;
      const started = await whisper.startListening(turn); assertTurn();
      if (!started) throw new Error("microphone_unavailable");
      setVoiceState("listening");
    } catch { if (current()) { setVoiceState("error"); setTimeout(() => { if (current()) setVoiceState("idle"); }, 1500); } }
    finally { if (current()) micLock.current = false; }
  }, [voiceState, whisper, tts, runChatStream, lang, resolveAgendaIntent, handlePaymentError]);

  const closeAgendaCapture = useCallback((prefill: CreateEventPrefill | LocalAgendaActionPrefill | null) => {
    const reviewed = modalCapture.current;
    // An old modal callback cannot dismiss or release a replacement review.
    if (!reviewed || reviewed.prefill !== prefill) return;
    resolvedRawTextsRef.current.delete(reviewed.rawKey);
    modalCapture.current = null; modalTurn.current = null;
    setLocalAgendaPrefill(null); setAgendaPrefill(null); setAgendaModalOpen(false);
  }, []);

  const confirmReceipt = useCallback(async (receipt: LocalAgendaReceiptReference) => {
    const reviewed = modalCapture.current, prefill = localAgendaPrefill ?? agendaPrefill;
    if (!reviewed || reviewed.prefill !== prefill || modalTurn.current !== reviewed.turn) return false;
    const turn = reviewed.turn;
    const isCurrent = () => modalCapture.current === reviewed && modalTurn.current === turn && turn.isCurrent();
    if (!isCurrent() || receipt.userId !== turn.ownerId || receipt.operation_id !== prefill?.operation_id || receipt.action_id !== prefill?.action_id || receipt.sessionId !== prefill?.expected_session_id) return false;
    const message = await confirmMirrorAgendaReceipt({ client: supabase, receipt, language: lang, isCurrent });
    if (!message || !isCurrent()) return false;
    setLastResponse(message.content); setLastResponseId(message.id); publishAgendaRefresh(turn.ownerId); return true;
  }, [agendaPrefill, localAgendaPrefill, lang]);

  return (
    <AgendaMirrorContext.Provider value={{
      voiceState,
      lastResponse,
      lastResponseId,
      clearLastResponse,
      handleMicPress,
      audioElement: tts.audioElement,
      alignment: tts.alignment,
    }}>
      <audio ref={audioElementRef} playsInline preload="auto" className="sr-only" />
      {children}
      {/* PATH 2 review-confirm modal — same component the Mirror principal
          mounts (MirrorChat.tsx:2225-2249). The modal owns its own INSERT.
          onCreated dispatches vyv:agenda-refresh so the page-level
          useAgendaSnapshot picks up the new row (Agenda.tsx listens).

          Portal to document.body so the modal escapes the transformed
          motion.div wrapper in Agenda.tsx:730 (`animate={{y:0}}` creates a
          stacking context + containing block that traps `position:fixed`
          descendants inside the MM card). Mirror principal doesn't need
          this — its root is a plain non-transformed div. */}
      {typeof document !== "undefined" && localAgendaPrefill && createPortal(
        <LocalAgendaActionModal open prefill={localAgendaPrefill} onClose={() => closeAgendaCapture(localAgendaPrefill)} onCreated={confirmReceipt} />, document.body,
      )}
      {typeof document !== "undefined" && createPortal(
        <CreateEventModal
          open={agendaModalOpen}
          onClose={() => closeAgendaCapture(agendaPrefill)}
          onCreated={() => undefined}
          onReceipt={confirmReceipt}
          defaultDate={new Date()}
          prefill={agendaPrefill}
        />,
        document.body,
      )}
    </AgendaMirrorContext.Provider>
  );
}

/* ── Avatar slot (row 1 left) ─────────────────────────────────────────── */

export function AgendaMiniMirrorAvatarSlot() {
  const { avatarUrl, loading: avatarLoading, hasAvatar } = useAvatar();
  // Escolha avatar × energia (user_profiles.mirror_surface) vale em TODA
  // superfície (bug 25/08). Predicado canônico: quem escolheu avatar sem ter
  // GLB próprio também cai na energia — nunca placeholder quebrado.
  const { surface, loading: surfaceLoading } = useMirrorSurface(hasAvatar);
  const showAvatar = surface === "avatar" && hasAvatar;
  const isMobile = useIsMobile();
  // Bug A fix (12/05/26): consume tts.audioElement + tts.alignment from the
  // Provider so AvatarCore's useLipSync activates during TTS playback.
  // AvatarViewer/AvatarCore are frozen; only the props we pass change.
  const { audioElement, alignment } = useAgendaMirrorState();

  // Bust framing values (Rafa 11/05/26) — aligned with Language Modo Professor.
  // Single number applied to both providers (no isMobile gate, no per-provider
  // object). isMobile is kept declared above to avoid breaking other consumers
  // in this file but intentionally not used in the 3 overrides below.
  void isMobile;

  return (
    <div className="w-[200px] md:w-[280px] self-stretch shrink-0">
      {/* Inner "mirror frame" — darker gradient + inset border + drop shadow
          creates the TV/screen illusion. Cross-module canonical pattern
          (Nutrition MM v3 / Fitness / Brain). */}
      <div
        className="relative w-full min-h-[220px] md:min-h-[300px] h-full rounded-2xl overflow-hidden"
        style={{
          background: "linear-gradient(135deg, #050811 0%, #0a0e1a 100%)",
          boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.06), inset 0 2px 8px rgba(0,0,0,0.4)",
        }}
      >
        {/* Radial cyan halo inside the frame, behind the avatar canvas. */}
        <div
          className="absolute inset-0 z-0 pointer-events-none"
          style={{
            background: "radial-gradient(circle at center, rgba(34,211,238,0.18) 0%, transparent 65%)",
          }}
        />
        {/* LIVE badge top-2 right-2 z-30 (canonical placement). */}
        <MiniMirrorLiveButton module="calendar" />
        {/* Avatar canvas */}
        <div className="relative z-10 w-full h-full">
          {avatarLoading || surfaceLoading ? (
            <div className="w-full h-full" aria-hidden="true" />
          ) : !showAvatar ? (
            <VyvEnergy state="idle" ratio={0.5} interactive={false} />
          ) : (
            <AvatarViewer
              modelUrl={avatarUrl}
              height="100%"
              enableRotate={false}
              framing={MINI_MIRROR_FRAMING}
              isAvaturnModel={MINI_MIRROR_IS_AVATURN_MODEL}
              animationUrl={getAnimationUrl("home")}
              scaleOverride={MINI_MIRROR_SCALE}
              cameraDistanceOverride={MINI_MIRROR_CAMERA_DISTANCE}
              lookAtYOverride={MINI_MIRROR_LOOK_AT_Y}
              audioElement={audioElement}
              alignment={alignment}
            />
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Hero + Chips slot (row 1 right) ──────────────────────────────────── */

type DrawerKind = "today" | "week" | "tasks" | null;

interface EventListItem {
  all_day_start?: string | null;
  all_day_end?: string | null;
  id: string;
  title: string;
  start_at: string;
  end_at: string | null;
  location: string | null;
}

interface AgendaMiniMirrorScorecardProps {
  todayCount: number;
  next7dCount: number;
  openTasksCount: number;
  nextEvent: NextEvent | null;
  loading?: boolean;
  error?: string | null;
  onRefresh?: () => void;
  // Tasks + CRUD from the page's single useTasks() instance (via
  // useAgendaSnapshot). The drawer rows mutate through THESE — never a
  // parallel query/write path — so the chip count, the drawer list and the
  // Tasks module all stay in sync (fix 10/07: drawer rows were read-only).
  tasks: Task[];
  onToggleTask: (id: string, snapshot?: TaskSnapshot) => Promise<void>;
  onUpdateTask: (id: string, fields: UpdateTaskInput, snapshot?: TaskSnapshot) => Promise<void>;
  onDeleteTask: (id: string, snapshot?: TaskSnapshot) => Promise<void>;
}

export function AgendaMiniMirrorScorecard({
  todayCount,
  next7dCount,
  openTasksCount,
  nextEvent,
  loading = false,
  error,
  onRefresh,
  tasks,
  onToggleTask,
  onUpdateTask,
  onDeleteTask,
}: AgendaMiniMirrorScorecardProps) {
  const { t, tf, lang } = useLanguage();

  // Drawer state — single source of truth for which sheet is open + lazy data.
  const [drawer, setDrawer] = useState<DrawerKind>(null);
  const [drawerRange, setDrawerRange] = useState(agendaEventRange("week"));
  const [drawerEvents, setDrawerEvents] = useState<EventListItem[]>([]);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerError, setDrawerError] = useState(false);
  const drawerRequest = useRef(0);
  const drawerTrigger = useRef<HTMLButtonElement | null>(null);
  const [taskLimit, setTaskLimit] = useState(50);
  useEffect(() => () => { drawerRequest.current += 1; }, []);

  // Task-row interactions (edit modal + delete confirm) — reuse the Tasks
  // module's components/pattern, no new write path.
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Task | null>(null);
  const [deleteSnapshot, setDeleteSnapshot] = useState<TaskSnapshot | null>(null);
  const [deletingTask, setDeletingTask] = useState(false);
  const [providerToggle, setProviderToggle] = useState<Task | null>(null);
  const pendingTasks = usePendingTasks();

  // Open tasks ordered by deadline, with undated tasks last. The drawer
  // reveals loaded rows progressively rather than silently discarding them.
  const openTasks = useMemo(() => {
    return tasks
      .filter((task) => !task.done)
      .slice()
      .sort((a, b) => {
        if (!a.due_date && !b.due_date) return 0;
        if (!a.due_date) return 1;
        if (!b.due_date) return -1;
        return a.due_date < b.due_date ? -1 : a.due_date > b.due_date ? 1 : 0;
      });
  }, [tasks]);

  const handleToggleTask = useCallback(async (id: string) => {
    const task = tasks.find(task => task.id === id);
    if (task && isSyncedTask(task)) { setDrawer(null); setProviderToggle(task); return; }
    try {
      const expected = task && snapshotForTask(task); if (!expected) throw new Error("invalid_snapshot"); await onToggleTask(id, expected);
    } catch (e) {
      // The row remains unchanged until the server confirms the mutation.
      console.error("[AgendaMM] task toggle failed:", e);
      toast.error(t("ux.tasks.mutationError"));
    }
  }, [onToggleTask, tasks, t]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;
    setDeletingTask(true);
    try {
      if (!deleteSnapshot) throw new Error("invalid_snapshot"); await onDeleteTask(deleteTarget.id, deleteSnapshot);
      setDeleteTarget(null);
    } catch (e) {
      // Same copy as the Tasks module's delete failure paths.
      console.error("[AgendaMM] task delete failed:", e);
      const blocked = e instanceof Error && e.message === "SYNCED_TASK_DELETE_BLOCKED";
      toast.error(t(blocked ? "tasks.deleteSyncedBlocked" : "tasks.deleteError"));
    } finally {
      setDeletingTask(false);
    }
  }, [deleteTarget, deleteSnapshot, onDeleteTask, t]);

  // Live countdown tick — re-renders the hero every 60s so "em 2h 15m" stays
  // current. setState on a tiny tick counter forces re-evaluation of
  // formatCountdown(nextEvent.start_at, lang) below.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((n) => (n + 1) % 1_000_000), 60_000);
    return () => clearInterval(id);
  }, []);

  const countdown = useMemo(
    () => {
      if (!nextEvent || !isAllDay(nextEvent)) return formatCountdown(nextEvent?.start_at ?? null, lang);
      const today = localEventDayKey(new Date().toISOString());
      const days = Math.round((Date.parse(nextEvent.all_day_start! + "T00:00:00Z") - Date.parse(today + "T00:00:00Z")) / 86400000);
      return new Intl.RelativeTimeFormat(localeTagFor(lang), { numeric: "auto" }).format(days, "day");
    },
    [nextEvent, lang, tick],
  );

  const meta = useMemo(() => {
    if (!nextEvent) return "";
    return isAllDay(nextEvent) ? [t("ux.agenda.allDay"), nextEvent.location].filter(Boolean).join(" · ")
      : formatEventMeta(nextEvent.start_at, nextEvent.end_at, nextEvent.location);
  }, [nextEvent, t]);

  const sourceLabel = useMemo(() => {
    if (!nextEvent) return "";
    return formatEventSource(nextEvent.external_provider, nextEvent.source);
  }, [nextEvent]);

  // ─── Drawer data fetchers (lazy on open) ───────────────────────────────
  // Tasks are NOT fetched here anymore — they come live from the shared
  // useTasks() instance via props (see openTasks above), so drawer mutations
  // and the chip count can never desync.
  const openDrawer = useCallback(async (kind: Exclude<DrawerKind, null>) => {
    const request = ++drawerRequest.current;
    setDrawer(kind);
    setTaskLimit(50);
    setDrawerError(false);
    setDrawerEvents([]);
    setDrawerLoading(kind !== "tasks");
    if (kind === "tasks") return;
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) throw authError ?? new Error("Session unavailable");
      const { start, end } = agendaEventRange(kind);
      setDrawerRange({ start, end });
      const { data, error } = await queryAgendaWindow(user.id, new Date(start), new Date(end));
      if (error) throw error;
      if (request === drawerRequest.current) setDrawerEvents((data as EventListItem[]) ?? []);
    } catch (e) {
      console.error("[AgendaMM] drawer fetch failed:", e);
      if (request === drawerRequest.current) setDrawerError(true);
    } finally {
      if (request === drawerRequest.current) setDrawerLoading(false);
    }
  }, []);

  // Empty score display per spec.
  const displayCountdown = loading || error ? "—" : countdown;

  return (
    <div className="flex-[1_1_220px] min-w-0 w-full flex flex-col justify-between">
      {error && <div role="alert" className="mb-3 text-sm text-white/80"><p>{t("ux.agenda.loadError")}</p><button className="min-h-11 underline text-cyan-200" onClick={onRefresh}>{t("ux.notes.retry")}</button></div>}
      {/* ── HERO: countdown + title + meta + source ─────────────────────── */}
      <div className="flex-1 min-w-0">
        <p
          className="font-display uppercase mb-2"
          style={{
            fontSize: "10px",
            letterSpacing: "0.25em",
            fontWeight: 500,
            color: "rgba(255,255,255,0.55)",
          }}
        >
          {t("agenda.miniMirror.next")}
        </p>
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0 leading-none min-w-0">
          <span
            className="font-display tabular-nums break-words"
            style={{
              fontSize: "clamp(22px, 5vw, 28px)",
              fontWeight: 200,
              letterSpacing: "-0.02em",
              color: "rgba(236,254,255,0.95)",
              lineHeight: 1.2,
            }}
          >
            {displayCountdown}
          </span>
        </div>
        {/* Subtle cyan accent line below countdown */}
        <div
          className="rounded-full mt-2 mb-2"
          style={{ width: 140, maxWidth: "100%", height: 2, background: "rgba(34,211,238,0.25)" }}
        />
        {nextEvent ? (
          <>
            <p
              className="break-words leading-snug"
              style={{
                fontSize: "16px",
                fontWeight: 500,
                color: "rgba(236,254,255,0.92)",
              }}
              title={nextEvent.title}
            >
              {nextEvent.title}
            </p>
            <p
              className="mt-1 break-words leading-relaxed"
              style={{
                fontSize: "12px",
                fontWeight: 400,
                color: "rgba(255,255,255,0.55)",
              }}
            >
              <time dateTime={isAllDay(nextEvent) ? nextEvent.all_day_start! : nextEvent.start_at}>{isAllDay(nextEvent) ? formatAllDayDates(nextEvent, localeTagFor(lang)) : eventStartDate(nextEvent).toLocaleDateString(localeTagFor(lang), { day: "numeric", month: "short", year: "numeric" })}</time>
              <br />{meta}
            </p>
            <p
              className="mt-1.5 uppercase break-words"
              style={{
                fontSize: "10px",
                letterSpacing: "0.08em",
                color: "rgba(255,255,255,0.45)",
              }}
            >
              {sourceLabel}
            </p>
          </>
        ) : (
          <p
            className="break-words leading-snug"
            style={{
              fontSize: "14px",
              fontWeight: 400,
              color: "rgba(255,255,255,0.55)",
            }}
          >
            {!loading && !error ? t("agenda.miniMirror.noNext") : "—"}
          </p>
        )}
      </div>

      {/* ── DIVIDER ─────────────────────────────────────────────────────── */}
      <div
        className="mt-3 mb-3"
        style={{ height: 0.5, background: "rgba(255,255,255,0.06)" }}
      />

      {/* ── CHIPS ROW (clickable, opens drawer) ─────────────────────────── */}
      <div className="flex gap-1.5">
        {[
          { kind: "today" as const, count: todayCount, label: t("agenda.miniMirror.today") },
          { kind: "week" as const, count: next7dCount, label: t("ux.agenda.next7") },
          { kind: "tasks" as const, count: openTasksCount, label: t("agenda.miniMirror.tasks") },
        ].map((c) => (
          <button
            key={c.kind}
            type="button"
            onClick={(event) => { drawerTrigger.current = event.currentTarget; void openDrawer(c.kind); }}
            className="flex-1 min-w-0 flex flex-col items-center justify-center rounded-xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-cyan-300 cursor-pointer px-1 py-3"
            style={{
              minHeight: 46,
              background: "rgba(255,255,255,0.04)",
              border: "0.5px solid rgba(255,255,255,0.08)",
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "rgba(255,255,255,0.08)"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "rgba(255,255,255,0.04)"; }}
            aria-haspopup="dialog"
            aria-expanded={drawer === c.kind}
            aria-label={`${c.label}: ${loading || (error && c.kind !== "tasks") ? "—" : c.count}`}
          >
            <span
              className="font-display tabular-nums"
              style={{ fontSize: 18, fontWeight: 200, color: "rgba(236,254,255,0.95)", lineHeight: 1 }}
            >
              {loading || (error && c.kind !== "tasks") ? "—" : c.count}
            </span>
            <span
              className="font-display mt-1 whitespace-normal break-words max-w-full leading-snug"
              style={{
                fontSize: 12,
                letterSpacing: "0",
                fontWeight: 500,
                color: "rgba(255,255,255,0.55)",
              }}
            >
              {c.label}
            </span>
          </button>
        ))}
      </div>

      {/* ── DRAWER: drill-down list (today / week / tasks) ───────────────── */}
      {/* Unmount the closed drawer before a provider sheet takes focus; its
          closing presence otherwise keeps the body's pointer lock alive. */}
      {!providerToggle && !(editingTask && isSyncedTask(editingTask)) && <Drawer open={drawer !== null} onOpenChange={(open) => { if (!open) { drawerRequest.current += 1; setDrawer(null); } }}>
        <DrawerContent
          className="rounded-t-3xl w-full max-w-2xl mx-auto max-h-[85dvh]"
          onCloseAutoFocus={(event) => { if (!editingTask && !deleteTarget) { event.preventDefault(); drawerTrigger.current?.focus(); } }}
          style={{
            background: "linear-gradient(135deg, #0a0e1a 0%, #0f1419 50%, #0a0e1a 100%)",
            border: "none",
          }}
        >
          <DrawerHeader className="text-left">
            <DrawerTitle className="text-white text-base font-display tracking-wide uppercase">
              {drawer === "today" && t("agenda.miniMirror.today")}
              {drawer === "week" && t("ux.agenda.next7")}
              {drawer === "tasks" && t("agenda.miniMirror.tasks")}
            </DrawerTitle>
            <DrawerDescription className="text-sm text-white/70">
              {t(drawer === "tasks" ? "ux.agenda.tasksHint" : "ux.agenda.eventsHint")}
            </DrawerDescription>
            <DrawerClose className="justify-self-end min-h-11 px-4 rounded-xl border border-white/20 text-sm text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-cyan-300">{t("common.close")}</DrawerClose>
          </DrawerHeader>
          <div className="px-4 pb-[max(1.5rem,env(safe-area-inset-bottom))] min-h-0 overflow-y-auto overscroll-contain">
            {drawerLoading ? (
              <div role="status" aria-label={t("common.loading")} className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-white/40" />
              </div>
            ) : drawerError ? (
              <div role="alert" className="space-y-3 py-4 text-sm text-white/80">
                <p>{t("ux.agenda.loadError")}</p>
                <button className="min-h-11 px-4 rounded-xl border border-cyan-300/40" onClick={() => drawer && openDrawer(drawer)}>{t("ux.notes.retry")}</button>
              </div>
            ) : drawer === "tasks" ? (
              openTasks.length === 0 ? (
                <p className="text-center text-white/50 py-8 text-sm">{t("ux.agenda.noTasks")}</p>
              ) : (
                <div className="space-y-2">
                  {openTasks.slice(0, taskLimit).map((task) => (
                    <div
                      key={task.id}
                      className="flex items-center gap-3 rounded-xl p-3"
                      style={{ background: "rgba(255,255,255,0.04)", border: "0.5px solid rgba(255,255,255,0.08)" }}
                    >
                      {/* Complete toggle — same confirmed mutation as the
                          Tasks module (useTasks.toggleTask via props). */}
                      <button
                        type="button"
                        onClick={() => handleToggleTask(task.id)}
                        disabled={pendingTasks.has(task.id)}
                        aria-busy={pendingTasks.has(task.id)}
                        className="w-11 h-11 flex items-center justify-center shrink-0 transition-colors active:scale-95"
                        aria-label={task.title}
                      >
                        {pendingTasks.has(task.id) ? <Loader2 size={17} className="animate-spin" /> : task.done ? (
                          <CheckCircle2 size={17} style={{ color: "rgba(34,211,238,0.85)" }} />
                        ) : (
                          <Circle size={17} style={{ color: "rgba(255,255,255,0.30)" }} />
                        )}
                      </button>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium break-words">{task.title}</p>
                        {task.due_date && (
                          <p className="text-white/45 text-[11px] mt-0.5">{task.due_date.slice(0, 10)}</p>
                        )}
                      </div>
                      {/* Edit/delete close the drawer first: the vaul drawer
                          lives in a body portal at z-50 and locks body
                          pointer-events, so stacking another sheet on top of
                          it is unreliable. Toggle (above) keeps it open. */}
                      <button
                        type="button"
                        onClick={() => { setDrawer(null); setEditingTask(task); }}
                        disabled={pendingTasks.has(task.id)}
                        className="w-11 h-11 rounded-lg flex items-center justify-center shrink-0 transition-opacity active:opacity-60"
                        style={{ background: "rgba(34,211,238,0.08)" }}
                        aria-label={t("tasks.modal.edit_title")}
                      >
                        <Pencil size={11} style={{ color: "rgba(34,211,238,0.70)" }} />
                      </button>
                      {/* Rule 30/06 (same as Tasks module): provider-synced
                          tasks get no delete — the next pull would resurrect
                          the row. */}
                      {!isSyncedTask(task) && (
                        <button
                          type="button"
                          onClick={() => { setDrawer(null); setDeleteSnapshot(snapshotForTask(task)); setDeleteTarget(structuredClone(task)); }}
                          disabled={pendingTasks.has(task.id)}
                          className="w-11 h-11 rounded-lg flex items-center justify-center shrink-0 transition-opacity active:opacity-60"
                          style={{ background: "rgba(248,113,113,0.08)" }}
                          aria-label={t("tasks.delete.confirm_button")}
                        >
                          <Trash2 size={11} style={{ color: "rgba(248,113,113,0.70)" }} />
                        </button>
                      )}
                    </div>
                  ))}
                  <p className="text-sm text-white/70 pt-2">{tf("ux.agenda.showing", { shown: Math.min(taskLimit, openTasks.length), total: openTasks.length })}</p>
                  {openTasks.length > taskLimit && <button className="min-h-11 w-full rounded-xl border border-white/20 text-sm text-white" onClick={() => setTaskLimit(n => n + 50)}>{t("ux.agenda.loadMore")}</button>}
                </div>
              )
            ) : drawerEvents.length === 0 ? (
              <p className="text-center text-white/50 py-8 text-sm">{t("ux.agenda.noEvents")}</p>
            ) : (
              <DrawerEventList events={drawerEvents} grouped={drawer === "week"} lang={lang} range={drawerRange} />
            )}
          </div>
        </DrawerContent>
      </Drawer>}

      {/* EDIT MODAL — the Tasks module's own modal, wired to the shared
          updateTask mutation. Portaled to body: this component sits inside
          animated (transformed) ancestors that would hijack its fixed
          positioning. */}
      {createPortal(
        <>
        {providerToggle && <ImportedTaskActionModal open task={providerToggle}
          action={providerToggle.done ? "reopen" : "complete"}
          onClose={() => setProviderToggle(null)} onCreated={onRefresh} />}
        <TaskModal
          open={editingTask !== null}
          task={editingTask}
          onClose={() => setEditingTask(null)}
          onUpdate={onUpdateTask}
          onRefreshReview={onRefresh}
        />
        </>,
        document.body,
      )}

      {/* DELETE CONFIRM — same bottom-sheet pattern/keys as Tasks.tsx,
          portaled for the same reason as the edit modal. */}
      {createPortal(
        <AnimatePresence>
        {deleteTarget && (
          <>
            <motion.div
              key="agenda-task-del-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[60] bg-black/60"
              onClick={() => { if (!deletingTask) setDeleteTarget(null); }}
            />
            <motion.div
              key="agenda-task-del-sheet"
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 28, stiffness: 300 }}
              className="fixed bottom-0 left-0 right-0 z-[70] rounded-t-3xl px-5 pt-5 pb-10"
              style={{
                background: "#10172a",
                border: "1px solid hsl(0 72% 51% / 0.25)",
                borderBottom: "none",
              }}
            >
              <div className="flex justify-center mb-5">
                <div className="w-10 h-1 rounded-full" style={{ background: "hsl(218 14% 30%)" }} />
              </div>
              <p className="text-[14px] font-display font-medium text-foreground mb-6 text-center">
                {t("tasks.delete.confirm_message")}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteTarget(null)}
                  disabled={deletingTask}
                  className="flex-1 py-3 rounded-xl font-display text-[13px] font-medium tracking-wide transition-all active:scale-95 disabled:opacity-50"
                  style={{
                    background: "hsl(218 14% 12%)",
                    border: "1px solid hsl(218 14% 20%)",
                    color: "hsl(218 14% 60%)",
                  }}
                >
                  {t("tasks.modal.cancel")}
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  disabled={deletingTask}
                  className="flex-1 py-3 rounded-xl font-display text-[13px] font-semibold tracking-wide transition-all active:scale-95 disabled:opacity-50"
                  style={{
                    background: "hsl(0 72% 51% / 0.85)",
                    color: "#fff",
                  }}
                >
                  {deletingTask ? "…" : t("tasks.delete.confirm_button")}
                </button>
              </div>
            </motion.div>
          </>
        )}
        </AnimatePresence>,
        document.body,
      )}
    </div>
  );
}

/* ── Inline event list for drawer (with optional day grouping) ─────────── */

export function DrawerEventList({
  events,
  grouped,
  lang,
  range,
}: {
  events: EventListItem[];
  grouped: boolean;
  lang: Lang;
  range: { start: string; end: string };
}) {
  if (!grouped) {
    return (
      <div className="space-y-2">
        {events.map((ev) => <EventRow key={ev.id} ev={ev} />)}
      </div>
    );
  }
  // Group by day key (YYYY-MM-DD)
  const groups = events.reduce<Record<string, EventListItem[]>>((acc, ev) => {
    if (isAllDay(ev)) {
      // Repeat only within the fetched window, including continuation days.
      for (const day = new Date(range.start); day < new Date(range.end); day.setDate(day.getDate() + 1)) {
        if (eventOccursOn(ev, day)) (acc[localEventDayKey(day.toISOString())] ||= []).push(ev);
      }
    } else {
      const key = localEventDayKey(ev.start_at);
      (acc[key] ||= []).push(ev);
    }
    return acc;
  }, {});
  const sortedKeys = Object.keys(groups).sort();
  const locale = localeTagFor(lang);
  return (
    <div className="space-y-5">
      {sortedKeys.map((key) => {
        const d = new Date(key + "T00:00:00");
        const dayLabel = d.toLocaleDateString(locale, { weekday: "short", day: "2-digit", month: "short" });
        return (
          <div key={key}>
            <p
              className="font-display uppercase mb-2"
              style={{
                fontSize: 11,
                letterSpacing: "0.18em",
                fontWeight: 500,
                color: "rgba(34,211,238,0.65)",
              }}
            >
              {dayLabel}
            </p>
            <div className="space-y-2">
              {groups[key].map((ev) => <EventRow key={ev.id} ev={ev} />)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function EventRow({ ev }: { ev: EventListItem }) {
  const { t } = useLanguage();
  const start = new Date(ev.start_at);
  const hh = String(start.getHours()).padStart(2, "0");
  const mm = String(start.getMinutes()).padStart(2, "0");
  const time = isAllDay(ev) ? t("ux.agenda.allDay") : `${hh}:${mm}`;
  let duration = "";
  if (ev.end_at && !isAllDay(ev)) {
    const durMs = new Date(ev.end_at).getTime() - start.getTime();
    if (durMs > 0) {
      const durMin = Math.round(durMs / 60000);
      const durH = Math.floor(durMin / 60);
      const remMin = durMin - durH * 60;
      duration = durH === 0 ? `${durMin}min` : remMin === 0 ? `${durH}h` : `${durH}h ${remMin}m`;
    }
  }
  return (
    <div
      className="flex items-center gap-3 rounded-xl p-3"
      style={{ background: "rgba(255,255,255,0.04)", border: "0.5px solid rgba(255,255,255,0.08)" }}
    >
      <div
        className="font-display tabular-nums shrink-0 text-center"
        style={{ width: isAllDay(ev) ? 76 : 52, fontSize: 13, fontWeight: 500, color: "rgba(34,211,238,0.85)" }}
      >
        {time}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-white text-sm font-medium break-words leading-relaxed">{ev.title}</p>
        {ev.location && (
          <p className="text-white/70 text-sm mt-1 flex items-start gap-1">
            <MapPin size={10} className="shrink-0" />
            <span className="break-words">{ev.location}</span>
          </p>
        )}
      </div>
      {duration && (
        <span className="text-white/45 text-[11px] shrink-0 tabular-nums">{duration}</span>
      )}
    </div>
  );
}

/* ── Voice slot (row 2 full width) ────────────────────────────────────── */

export function AgendaMiniMirrorVoiceSlot() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { voiceState, lastResponse, lastResponseId, clearLastResponse, handleMicPress } = useAgendaMirrorState();
  const { isReported } = useReportedContent();

  const micLabel = (() => {
    if (voiceState === "listening") return t("agenda.mirror.listening");
    if (voiceState === "processing") return t("agenda.mirror.processing");
    if (voiceState === "error") return t("agenda.mirror.error");
    return t("agenda.mirror.tapToTalk");
  })();

  const micClass = (() => {
    if (voiceState === "listening") return "bg-cyan-500 ring-2 ring-cyan-300 animate-pulse scale-110 shadow-[0_0_28px_rgba(34,211,238,0.6)]";
    if (voiceState === "processing") return "bg-cyan-500/50 ring-2 ring-cyan-400/50 shadow-[0_0_24px_rgba(34,211,238,0.35)]";
    if (voiceState === "speaking") return "bg-cyan-500/60 ring-2 ring-cyan-300/60 shadow-[0_0_24px_rgba(34,211,238,0.45)]";
    if (voiceState === "error") return "bg-red-500/60 ring-2 ring-red-300/60";
    // Idle — base style + idle-breathing class (canonical Nutrition MM v2 pattern).
    return "bg-cyan-500/70 ring-2 ring-cyan-400/60 hover:bg-cyan-500 agenda-mm-idle-breathe";
  })();

  return (
    <div className="flex flex-col items-center gap-2 w-full">
      {/* Idle breathing animation — scale + outer cyan glow pulse (2.5s loop).
          Mirrors the Nutrition MM v2 canonical animation. */}
      <style>{`
        @keyframes agenda-mm-idle-breathe {
          0%, 100% { transform: scale(1); box-shadow: 0 0 18px rgba(34,211,238,0.40); }
          50%      { transform: scale(1.03); box-shadow: 0 0 28px rgba(34,211,238,0.20); }
        }
        .agenda-mm-idle-breathe { animation: agenda-mm-idle-breathe 2.5s ease-in-out infinite; }
      `}</style>
      <button
        type="button"
        onClick={handleMicPress}
        className={`w-14 h-14 rounded-full flex items-center justify-center transition-all ${micClass}`}
        aria-label={micLabel}
        title={micLabel}
      >
        {voiceState === "processing" ? (
          <Loader2 className="w-6 h-6 text-white animate-spin" />
        ) : (
          <Mic className="w-6 h-6 text-white" />
        )}
      </button>

      <p className="text-[10px] uppercase tracking-widest text-foreground/40">{micLabel}</p>

      {lastResponse && !isReported(REPORT_SURFACES.MIRROR_MINI, lastResponseId) && (
        // Tap anywhere on the card dismisses it (does NOT navigate); auto-clears
        // on the shared CARD_DISMISS_MS timer regardless. "See more" is the only
        // element that navigates — it stops propagation so it doesn't also close.
        <div
          role="button"
          tabIndex={0}
          aria-label={t("common.close")}
          onClick={clearLastResponse}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              clearLastResponse();
            }
          }}
          className="max-w-md text-center transition-opacity duration-200 px-4 cursor-pointer"
        >
          <p className="text-sm text-foreground/85 line-clamp-2 leading-snug">{lastResponse}</p>
          <div className="mt-1 flex items-center justify-center gap-1">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              navigate("/mirror");
            }}
            className="text-[11px] text-cyan-300 hover:text-cyan-200"
          >
            {t("agenda.mirror.seeMore")}
          </button>
          {/* Denuncia. O ReportContentButton ja faz stopPropagation no clique,
              entao abrir a folha nao dispara o clearLastResponse do card. */}
          {lastResponseId && (
            <ReportContentButton
              surface={REPORT_SURFACES.MIRROR_MINI}
              contentId={lastResponseId}
              snapshot={lastResponse}
              context={{ module: "agenda" }}
            />
          )}
          </div>
        </div>
      )}
    </div>
  );
}
